<template>
  <div class="container-modal">
    <div class="container-chatmodal">
      <div class="make-tit-wrap">
        <div class="step-flex">
          <div class="ms-tit">
            <div v-if="currentStep" class="tit mb-2">
              <i18n-t :keypath="CHAT_STEP_TITLE[currentStep].keypath" scope="global">
                <template v-if="CHAT_STEP_TITLE[currentStep].keyword[0]" #keyword>
                  <span class="bold">{{ t(CHAT_STEP_TITLE[currentStep].keyword[0]) }}</span>
                </template>
                <template v-if="CHAT_STEP_TITLE[currentStep].keyword[1]" #keyword2>
                  <span class="bold">{{ t(CHAT_STEP_TITLE[currentStep].keyword[1]!) }}</span>
                </template>
              </i18n-t>
            </div>

            <span v-if="currentStep === 'OPTIONAL_MODE'" class="dim">{{ t('select_or_skip_option') }}</span>
          </div>
        </div>
      </div>

      <div class="make-content">
        <div class="make-group" :class="{ open: progressedSteps.includes('SOURCE_LANGUAGE'), 'ani-del': isFirstStep }">
          <p class="s-font pdb-2">{{ t('entrance_intro_message_1') }}</p>
          <Info2Column>
            <button class="change-sel-btn flex-btw pd-horizontal-3" @click="handleLanguageSelectPopup('LANGUAGE_HOME')">
              {{ sourceLanguage?.name || t('choose_language') }}
              <IconArrow class="icon" />
            </button>
          </Info2Column>
        </div>

        <div class="make-group" :class="{ open: progressedSteps.includes('TARGET_LANGUAGE'), 'ani-del': isFirstStep }">
          <p class="s-font pdb-2">{{ t('single-nickname-away') }}</p>
          <Info2Column>
            <button class="change-sel-btn flex-btw pd-horizontal-3" @click="handleLanguageSelectPopup('LANGUAGE_AWAY')">
              {{ targetLanguage?.name || t('choose_language') }}
              <IconArrow class="icon" />
            </button>
          </Info2Column>
        </div>

        <template v-if="mode === 'CREATE'">
          <div
            class="make-group personnel"
            :class="{ open: progressedSteps.includes('MAX_PARTICIPANTS'), 'ani-del': isFirstStep }"
          >
            <Info2Column v-if="participantOptions.length && callType !== 'MEETING'">
              <div class="radio-options">
                <label
                  v-for="(option, optionIndex) in participantOptions"
                  :key="`option_${optionIndex}`"
                  class="radio-option"
                  @click="nextStep('MAX_PARTICIPANTS')"
                >
                  <input v-model="form.maxParticipants" type="radio" name="participants-number" :value="option" />
                  <div class="radio-txt">{{ formatNumberWithComma(option) }}{{ t('label_person_count') }}</div>
                </label>
              </div>
            </Info2Column>
          </div>

          <div
            class="make-group mode"
            :class="{ open: progressedSteps.includes('OPTIONAL_MODE'), 'ani-del': isFirstStep }"
          >
            <Info2Column v-if="['MEETING', 'CONFERENCE'].includes(callType)">
              <div class="wrapper-checkbox" @click="nextStep('OPTIONAL_MODE')">
                <CheckboxImage
                  v-model="form.options.screenMode"
                  :image-url="getAssetImageUrl('common/icons/screen-icon.svg')"
                >
                  {{ t('screen_view') }}
                </CheckboxImage>
                <CheckboxImage
                  v-model="form.options.subtitleMode"
                  :image-url="getAssetImageUrl('common/icons/subtitle-icon.svg')"
                >
                  {{ t('subtitle_view') }}
                </CheckboxImage>
              </div>
            </Info2Column>

            <div class="btn-wrap">
              <button
                v-if="currentStep === 'OPTIONAL_MODE'"
                type="button"
                class="gray-btn m-btn width-auto"
                @click="nextStep('OPTIONAL_MODE')"
              >
                {{ t('label_skip') }}
              </button>
            </div>
          </div>

          <div class="make-group name" :class="{ open: progressedSteps.includes('CHAT_NAME'), 'ani-del': isFirstStep }">
            <div class="input-close-wrap">
              <Info2Column>
                <input
                  v-model="form.title"
                  type="text"
                  class="input-t"
                  :maxlength="MAX_CHAT_TITLE_LENGTH"
                  :placeholder="t('alert_enter_interpretation_chatroom_name')"
                />
                <button v-if="form.title" type="button" class="close" @click="resetChatName">
                  <img :src="getAssetImageUrl('common/icons/close.svg')" alt="" />
                </button>
              </Info2Column>

              <div class="flex-btw mt-2">
                <span class="asterisk">{{ t('label_possible_validation') }}</span>
                <span>{{ form.title.length }}/{{ MAX_CHAT_TITLE_LENGTH }}</span>
              </div>

              <div
                v-if="
                  form.title && (form.title.length < MIN_CHAT_TITLE_LENGTH || form.title.length > MAX_CHAT_TITLE_LENGTH)
                "
                class="error-message"
              >
                <IconInfo class="icon-error" />
                <p class="error">
                  {{ t('alert_name_length_limit') }}
                </p>
              </div>
            </div>
          </div>
        </template>

        <button v-if="!isLastStep" type="button" class="gray-btn l-btn" @click="startQuickChat">
          {{ t('label_quick_start') }}
        </button>
      </div>

      <div class="footer ani-del" :class="{ open: isLastStep }">
        <div class="foot-info-wrap mb-3">
          <div v-if="unitPrice" class="fi-top">
            <p class="accent s-font">
              <i18n-t keypath="remaining_left" scope="global">
                <template #remaining>
                  <span class="primary">
                    <i18n-t keypath="hours_minutes" scope="global">
                      <template #hours>{{ formatNumberWithComma(timeRemaining.hours) }}</template>
                      <template #minutes>{{ timeRemaining.minutes }}</template>
                    </i18n-t>
                  </span>
                </template>
              </i18n-t>
            </p>
            <p class="s-font">
              <i18n-t keypath="label_deduct_credit_per_minute">
                <template #credit>
                  <span class="primary"> {{ formatNumberWithComma(unitPrice) }} </span>
                </template>
              </i18n-t>
            </p>
          </div>

          <div v-if="UserType.isHost(userStore.userInfo.userType)" class="fi-bottom">
            <button type="button" class="credit-buy-btn m-btn" @click="handleCreditPurchase">
              <img :src="getAssetImageUrl('common/icons/credit-white-icon.svg')" alt="" />
              <span>{{ t('ss_invoice_type_topup') }}</span>
            </button>
          </div>
        </div>

        <ButtonProcess full-width :loading="loading" @click="handleChatAction">
          {{ t('label_start_interpretation') }}
        </ButtonProcess>
      </div>
    </div>

    <teleport to="body">
      <ModalSelectLanguage
        v-if="['LANGUAGE_HOME', 'LANGUAGE_AWAY'].includes(visibleModalType)"
        :current-language="selectedLanguage"
        :disabled-code="languageModalProperties.disabledCode"
        :subtitle-key="languageModalProperties.subtitleKey"
        :languages="languages"
        using-custom-select
        @select="selectLanguage"
        @close="handleLanguageSelectPopup('')"
      />
    </teleport>
  </div>
</template>

<script setup lang="ts">
import { cloneDeep, isEmpty } from 'lodash';

import { createChatRoom } from '@/api/apiService';
import ModalSelectLanguage from '@/components/domains/chat/common/ModalSelectLanguage.vue';
import IconArrow from '@/components/icons/IconArrow.vue';
import IconInfo from '@/components/icons/IconInfo.vue';
import ButtonProcess from '@/components/shared/base/ButtonProcess.vue';
import CheckboxImage from '@/components/shared/base/CheckboxImage.vue';
import Info2Column from '@/components/shared/composite/Info2Column.vue';
import { useGtag } from '@/composables/shared/useGtag';
import { usePayment } from '@/composables/shared/usePayment';
import { getLanguageByCode } from '@/static/languages';
import { useAlertStore } from '@/stores/alert';
import { useAudioStore } from '@/stores/audio';
import { useLanguageStore } from '@/stores/language';
import { usePaymentStore } from '@/stores/payment';
import { useSubscriptionStore } from '@/stores/subscription';
import { useToastStore } from '@/stores/toast';
import { useUserStore, UserType } from '@/stores/user';
import { Language, Nullable, TypeCall } from '@/types/common';
import { ChatCreateStepName } from '@/types/components';
import { IResponseContractCustomerPricing, IResponseMultiCallsDashboard } from '@/types/response';
import { DATE_FORMAT, formatDateByLocale } from '@/utils/common/formatDateByLocale';
import {
  CHAT_CREATE_STEPS,
  CHAT_KIND,
  CHAT_OPTION_PRESETS,
  CHAT_PRICE_KEY_MAP,
  CHAT_STEP_TITLE,
  DEFAULT_CHAT_CREATE_FORM,
  MIN_CHAT_TITLE_LENGTH,
  MAX_CHAT_TITLE_LENGTH,
  SERVICE_PRICE_MAP,
} from '@/utils/constants/chat';
import { getAssetImageUrl } from '@/utils/getAssetImageUrl';
import { formatNumberWithComma } from '@/utils/stringUtils.js';
import { validateName } from '@/utils/validators';

const props = withDefaults(
  defineProps<{
    title: string;
    mode: 'CREATE' | 'ENTER';
    callType: TypeCall;
    callInfo?: IResponseMultiCallsDashboard;
    unitPriceList: IResponseContractCustomerPricing[];
  }>(),
  {
    title: '',
    mode: 'CREATE',
    callType: 'GENERAL',
    callInfo: () => ({}) as IResponseMultiCallsDashboard,
    unitPriceList: () => [],
  }
);
const emit = defineEmits<{
  create: [res: any];
  enter: [planId: string, sourceLanguage: string, targetLanguage: string];
}>();

const { t } = useI18n();
const route = useRoute();

const toastStore = useToastStore();
const alertStore = useAlertStore();
const audioStore = useAudioStore();
const userStore = useUserStore();
const paymentStore = usePaymentStore();
const subscriptionStore = useSubscriptionStore();
const languageStore = useLanguageStore();

const { openCreditBuyPopup, hasRegisteredCard, openCardRegisterPopup, openCardManagePopup } = usePayment();
const { trackEventFunnel } = useGtag();

const loading = ref<boolean>(false);
const form = ref(cloneDeep(DEFAULT_CHAT_CREATE_FORM));
const visibleModalType = ref<'LANGUAGE_HOME' | 'LANGUAGE_AWAY' | ''>('');
const defaultTitle = ref<string>('');
const currentStep = ref<ChatCreateStepName | null>(null);

const sourceLanguage = ref<Language>(getLanguageByCode(userStore.userInfo.languageCode));
const targetLanguage = ref<Language | null>(null);
const selectedLanguage = ref<Language | null>(null);

const deniedPlanKeys = computed<string[]>(() => {
  return (
    {
      FREE:
        subscriptionStore.currentCredits <= 1000 ? ['SCB_P300', 'SCB_P500', 'SCB_P1000', 'SCB_P2000', 'SCB_P3000'] : [],
    }[subscriptionStore.currentPlan] || []
  );
});

const participantOptions = computed<number[]>(() => {
  return Object.values(SERVICE_PRICE_MAP).reduce((acc: number[], { prefix, limitAttendee = 0 }) => {
    if (form.value.id === prefix && !deniedPlanKeys.value.includes(`${prefix}${limitAttendee}`)) {
      acc.push(limitAttendee);
    }
    return acc;
  }, []);
});

const languages = computed<Language[]>(() => {
  return languageStore.interpretationLanguageOptions.textOutputEnabled;
});

const steps = computed<ChatCreateStepName[]>(() => CHAT_CREATE_STEPS[props.callType]);
const currentStepIndex = computed<number>(() => (currentStep.value ? steps.value.indexOf(currentStep.value) : -1));
const progressedSteps = computed<ChatCreateStepName[]>(() => steps.value.slice(0, currentStepIndex.value + 1));
const isFirstStep = computed<boolean>(() => currentStep.value === steps.value.at(0));
const isLastStep = computed<boolean>(() => currentStep.value === steps.value.at(-1));

const nextStep = (stepName: ChatCreateStepName) => {
  if (stepName && currentStep.value !== stepName) return;

  const next = steps.value[currentStepIndex.value + 1];
  if (next) {
    currentStep.value = next;
  }
};

const startQuickChat = () => {
  form.value.maxParticipants = participantOptions.value[0];
  currentStep.value = CHAT_CREATE_STEPS[form.value.callType].at(-1) ?? null;

  handleChatAction();
};

const resetChatName = () => {
  form.value.title = '';
};

const unitPrice = computed<number | null>(() => {
  const { id = '', maxParticipants = '' } = form.value;
  const itemPrice = props.unitPriceList.find(({ planId }) => planId === `${id}${maxParticipants}`);

  if (!itemPrice) {
    return null;
  }

  if (isEmpty(itemPrice.options)) {
    return itemPrice.unitCredit;
  }

  const { SCREEN = 0, SUBTITLE = 0 } = itemPrice.options;
  let priceFinal = itemPrice.unitCredit;

  if (form.value.options.screenMode) {
    priceFinal += SCREEN;
  }

  if (form.value.options.subtitleMode) {
    priceFinal += SUBTITLE;
  }

  return priceFinal;
});

const timeRemaining = computed<{ hours: number; minutes: number }>(() => {
  const totalMinutes = unitPrice.value ? subscriptionStore.currentCredits / unitPrice.value : 0;

  return {
    hours: Math.floor(totalMinutes / 60),
    minutes: Math.floor(totalMinutes % 60),
  };
});

const languageModalProperties = computed<Record<string, string>>(() => {
  const isAway = visibleModalType.value === 'LANGUAGE_AWAY';

  return {
    disabledCode: isAway ? sourceLanguage.value?.code : targetLanguage.value?.code,
    subtitleKey: isAway ? 'please_select_language_away' : 'please_select_language',
  };
});

const initChatForm = () => {
  currentStep.value = props.callType === 'SINGLE' ? 'TARGET_LANGUAGE' : steps.value[0];
  defaultTitle.value = formatDateByLocale(Date.now(), DATE_FORMAT.FULL, userStore.pageLanguage);

  form.value = {
    ...form.value,
    id: CHAT_PRICE_KEY_MAP[props.callType],
    title: defaultTitle.value,
    callType: props.callType,
    options: { ...(CHAT_OPTION_PRESETS[props.callType] ?? DEFAULT_CHAT_CREATE_FORM.options) },
  };

  form.value.maxParticipants = ['CONFERENCE', 'GENERAL'].includes(form.value.callType)
    ? 0
    : participantOptions.value[0];

  sourceLanguage.value = getLanguageByCode(userStore.userInfo.languageCode);
  targetLanguage.value = null;
};

const handleLanguageSelectPopup = (type) => {
  visibleModalType.value = type;

  selectedLanguage.value =
    {
      LANGUAGE_HOME: sourceLanguage.value,
      LANGUAGE_AWAY: targetLanguage.value,
    }[type] || '';
};

const selectLanguage = (language: Language) => {
  if (visibleModalType.value === 'LANGUAGE_HOME') {
    sourceLanguage.value = language;
  } else if (visibleModalType.value === 'LANGUAGE_AWAY') {
    targetLanguage.value = language;
  } else {
    audioStore.selectLanguage(language.code);
  }
};

const validateChatForm = (): Nullable<string> => {
  if (!sourceLanguage.value) {
    return 'error_input_no_language';
  }

  if (CHAT_KIND.isSingle(props.callType) && !targetLanguage.value) {
    return 'please_select_language_away';
  }

  if (CHAT_KIND.isOneOf(props.callType, [CHAT_KIND.CONFERENCE, CHAT_KIND.MEETING, CHAT_KIND.GENERAL])) {
    return validateName(form.value.title);
  }

  return null;
};

const getChatFormData = (): FormData => {
  const { id = '', callType = '', title = '', options, maxParticipants = '' } = form.value;

  const formData = new FormData();
  formData.append(
    'body',
    JSON.stringify({
      plan: `${id}${maxParticipants}`,
      callType,
      title,
      description: '',
      keywords: [],
      options,
      removeEntranceLogoImage: false,
      removeCallLogoImage: false,
    })
  );
  return formData;
};

const createChat = async () => {
  const { data, success, errorCode } = await createChatRoom(getChatFormData());

  if (success) {
    toastStore.info(t('alert_create_success'));

    emit('create', { ...data[0], sourceLanguage: sourceLanguage.value.code });
  } else if (errorCode === 1021) {
    toastStore.error(t('alert_duplicate_name_exists'));
  } else {
    toastStore.error(t('failed_to_login_for_unknown_reason'));
  }
};

const enterChat = () => {
  const id = CHAT_PRICE_KEY_MAP[props.callType];
  emit('enter', `${id}1`, sourceLanguage.value.code, targetLanguage.value?.code ?? '');
};

const handleChatAction = async () => {
  const errorMessage = validateChatForm();
  if (errorMessage) {
    alertStore.alert({
      title: t('guide'),
      message: t(errorMessage),
    });

    return;
  }

  loading.value = true;

  if (props.mode === 'CREATE') {
    await createChat();
  } else if (props.mode === 'ENTER') {
    enterChat();
  }

  loading.value = false;
};

const handleCreditPurchase = async () => {
  trackEventFunnel('click_credit_buy_button');

  if (!hasRegisteredCard()) {
    const registeredCardType = await openCardRegisterPopup();
    if (!registeredCardType) {
      return;
    }
  }

  if (subscriptionStore.lastPaymentErrorCode) {
    const selectedCardType = await openCardManagePopup('PAYMENT', subscriptionStore.paymentType);
    if (!selectedCardType) {
      return;
    }
  }

  openCreditBuyPopup();
};

onMounted(async () => {
  sourceLanguage.value = getLanguageByCode(userStore.userInfo.languageCode);

  const { languageAway = false } = route.query;
  if (languageAway) {
    visibleModalType.value = 'LANGUAGE_AWAY';
  }

  await paymentStore.fetchUserCardList();
});

watch(
  () => props.callType,
  () => {
    initChatForm();
  },
  { immediate: true }
);

watch(
  () => userStore.pageLanguage,
  () => {
    if (defaultTitle.value === form.value.title) {
      defaultTitle.value = formatDateByLocale(Date.now(), DATE_FORMAT.FULL, userStore.pageLanguage);
      form.value.title = defaultTitle.value;
    }
  }
);
</script>

<style lang="scss" scoped>
.container-modal {
  display: flex;
  position: relative;
  border-radius: 16px;
  width: 100%;
  padding: 2rem 1.5rem 1.5rem;
  margin: 0 auto;
  background: #fff;
  justify-content: center;
  box-shadow: 2px 2px 20px rgba(71, 95, 216, 0.15);
  height: auto;
  transition: height 0.5s cubic-bezier(0.4, 0, 0.3, 1);
}
.container-chatmodal {
  width: 100%;
  background: var(--surface-glass-dim);
  border-radius: 10px;

  .wrapper-title {
    display: flex;
    gap: 0.5rem;
    align-items: center;
  }
  .make-tit-wrap {
    .step-flex {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      &.off {
        display: none;
      }
    }
    .ms-tit {
      padding-bottom: $spacing6;
      .tit {
        font-size: $font-size-h4;
        line-height: 140%;
      }
      &.off {
        display: none;
      }
    }
  }
  .make-group {
    max-height: 0;
    opacity: 0;
    pointer-events: none;
    transform: translateY(-50px);
    .content-wrapper {
      display: block;
      margin: 0;
    }
    &.open {
      transition:
        transform 0.5s cubic-bezier(0.4, 0, 0.3, 1),
        opacity 1s cubic-bezier(0.4, 0, 0.3, 1);
      transform: translateY(0);
      max-height: 500px;
      opacity: 1;
      pointer-events: auto;
      margin-bottom: $spacing3;
      &.ani-del {
        transition: none;
      }
    }
    &.name {
      .flex-btw {
        padding: 0 $spacing1;
        span {
          font-size: $font-size-psmall;
          color: $color-gray-200;
        }
      }
    }
    &.mode {
      .btn-wrap {
        margin-top: $spacing2;
        display: flex;
        justify-content: flex-end;
      }
    }
  }
  .footer {
    opacity: 0;
    pointer-events: none;
    height: 0;
    overflow: hidden;
    &.open {
      transition:
        height 0.5s cubic-bezier(0.4, 0, 0.3, 1),
        opacity 0.5s cubic-bezier(0.4, 0, 0.3, 1);
      opacity: 1;
      pointer-events: auto;
      height: auto;
      padding-top: $spacing6;
    }
    // &.ani-del {
    //   transition: none;
    // }
  }

  .foot-info-wrap {
    background: $color-navy-5;
    padding: 1rem;
    border-radius: $size-s;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: $size-s;
    .fi-bottom {
      .credit-buy-btn {
        background: #ffd609;
        padding: 6px 16px 6px 6px;
        gap: $spacing1;
        border-radius: 100px;
        span {
          font-size: $font-size-psmall;
          font-weight: 600;
        }
      }
    }
  }
  .auto-wrap {
    display: flex;
    align-items: center;
    gap: $size-s;
    p {
      white-space: nowrap;
      font-size: $font-size-psmall;
    }
  }
}

.radio-options {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  .radio-option {
    border: 1px solid $color-gray-40;
    padding: $spacing1 $spacing2;
    border-radius: $spacing2;
    background: #fff;
    &:has(input:checked) {
      border: 1px solid $color-navy-100;
      background: $color-navy-5;
      .radio-txt {
        color: $color-navy-100;
      }
    }
    input[type='radio'] {
      display: block;
    }
  }

  .radio-box {
    justify-content: center;
  }
}

.wrapper-checkbox {
  display: flex;
  gap: $spacing2;
}

.error-message {
  display: flex;
  gap: 0.25rem;
  align-items: center;
  margin-top: 0.5rem;
}

::v-deep(.content-wrapper) {
  flex-direction: column;
}

::v-deep(.title) {
  margin-top: 0;
}

::v-deep(.content-slot) {
  flex: unset;
}

.change-sel-btn {
  background: $gray10;
  height: 3rem;
  font-size: $font-size-psmall;
  width: 100%;
  border-radius: $size-s;
  svg {
    transform: rotate(90deg);
  }
}

.input-error {
  border-color: var(--error);
  box-shadow: none;
}

.input-t {
  padding-right: 2.75rem;
}

.input-close-wrap {
  :deep(.content-slot) {
    position: relative;
    .close {
      position: absolute;
      right: 0.75rem;
      top: 50%;
      transform: translateY(-50%);
      padding: 0.25rem;
    }
  }
}

@include respond-to(mobile-landscape) {
  .container-modal {
    height: fit-content;
    flex: 0 0 auto;
    -webkit-overflow-scrolling: touch;
    padding: 1.5rem 1rem 1rem;
  }
  .container-chatmodal {
    background: none;
    height: 100%;
    .make-tit-wrap {
      .ms-tit {
        .tit {
          font-size: $font-size-h6;
        }
      }
    }
  }
}
</style>
